import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCircleArrowLeft } from "react-icons/fa6";
import { FileText, CheckCircle, AlertCircle, Scale, Users, Ban } from 'lucide-react';

function TermsAndConditions() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const styles = `
        .terms-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%);
            padding: 2rem 1rem;
        }

        .terms-card {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            border-radius: 24px;
            padding: 2.5rem;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
            border: 1px solid rgba(6, 182, 212, 0.15);
        }

        .terms-title {
            font-size: 2.5rem;
            font-weight: 800;
            background: linear-gradient(90deg, #6366f1, #06b6d4);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 0.5rem;
        }

        .last-updated {
            color: #666;
            font-size: 0.9rem;
            margin-bottom: 2rem;
        }

        .section-title {
            color: #0e4aa5;
            font-weight: 700;
            font-size: 1.3rem;
            margin: 1.5rem 0 1rem 0;
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .section-icon {
            width: 32px;
            height: 32px;
            background: linear-gradient(135deg, #6366f1, #06b6d4);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
        }

        .terms-text {
            color: #444;
            line-height: 1.8;
            font-size: 1rem;
            margin-bottom: 1rem;
            text-align: left;
            font-weight: bold;
        }

        .terms-list {
            list-style: none;
            padding-left: 0;
            margin-left: 0;
            text-align: left;
        }

        .terms-list li {
            color: #555;
            line-height: 1.8;
            margin-bottom: 0.5rem;
            position: relative;
            padding-left: 1.8rem;
            text-align: left;
        }

        .terms-list li::before {
            content: "•";
            color: #06b6d4;
            font-weight: bold;
            position: absolute;
            left: 0.4rem;
        }

        @media (max-width: 768px) {
            .terms-card {
                padding: 1.5rem;
            }
            .terms-title {
                font-size: 2rem;
            }
            .section-title {
                font-size: 1.2rem;
            }
        }
    `;

    return (
        <div className="terms-container">
            <style>{styles}</style>
            <div className="container" style={{ maxWidth: 900 }}>
                <button
                    className="btn btn-outline-primary d-flex align-items-center mb-4"
                    onClick={() => navigate('/')}
                    style={{
                        borderRadius: '12px',
                        padding: '10px 20px',
                        borderWidth: '2px',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#0d53bd';
                        e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#0d53bd';
                    }}
                >
                    <FaCircleArrowLeft className="me-2" size={18} />
                    Back to Home
                </button>

                <div className="terms-card">
                    <h1 className="terms-title">Terms & Conditions</h1>
                    <p className="last-updated">Last Updated: April 20, 2026</p>

                    <p className="terms-text">
                        Welcome to InsightDraft AI. By accessing or using our platform, you agree to be bound by 
                        these Terms and Conditions. Please read them carefully before using our services.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><CheckCircle size={18} /></span>
                        Acceptance of Terms
                    </div>
                    <p className="terms-text">
                        By creating an account or using InsightDraft AI's services, you acknowledge that you have read, 
                        understood, and agree to be bound by these Terms. If you do not agree, please do not use our platform.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><FileText size={18} /></span>
                        Description of Service
                    </div>
                    <p className="terms-text">
                        InsightDraft AI provides AI-powered business analysis tools that generate insights, strategies, 
                        and recommendations based on information you provide. Our services include:
                    </p>
                    <ul className="terms-list">
                        <li>Business idea analysis and validation</li>
                        <li>Existing business optimization recommendations</li>
                        <li>Market research and competitive analysis</li>
                        <li>Financial projections and growth strategies</li>
                        <li>Downloadable PDF reports</li>
                    </ul>

                    <div className="section-title">
                        <span className="section-icon"><Users size={18} /></span>
                        User Accounts
                    </div>
                    <p className="terms-text">
                        When you create an account, you agree to:
                    </p>
                    <ul className="terms-list">
                        <li>Provide accurate and complete information</li>
                        <li>Maintain the security of your account credentials</li>
                        <li>Notify us immediately of any unauthorized access</li>
                        <li>Accept responsibility for all activities under your account</li>
                    </ul>

                    <div className="section-title">
                        <span className="section-icon"><Scale size={18} /></span>
                        Intellectual Property
                    </div>
                    <p className="terms-text">
                        All content, features, and functionality on InsightDraft AI, including but not limited to 
                        text, graphics, logos, and software, are owned by us and protected by copyright and 
                        intellectual property laws. You may not reproduce, distribute, or create derivative works 
                        without our express permission.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><AlertCircle size={18} /></span>
                        Disclaimer of Warranties
                    </div>
                    <p className="terms-text">
                        Our AI-generated analysis and recommendations are provided "as is" for informational purposes only. 
                        We do not guarantee:
                    </p>
                    <ul className="terms-list">
                        <li>Accuracy or completeness of AI-generated content</li>
                        <li>Business success or financial outcomes</li>
                        <li>Uninterrupted or error-free service</li>
                        <li>Compatibility with all devices or browsers</li>
                    </ul>

                    <div className="section-title">
                        <span className="section-icon"><Ban size={18} /></span>
                        Limitation of Liability
                    </div>
                    <p className="terms-text">
                        InsightDraft AI shall not be liable for any indirect, incidental, special, consequential, 
                        or punitive damages resulting from your use or inability to use our services. Our total 
                        liability shall not exceed the amount paid by you for our services in the past 12 months.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><AlertCircle size={18} /></span>
                        Termination
                    </div>
                    <p className="terms-text">
                        We reserve the right to suspend or terminate your account at our discretion if you violate 
                        these Terms or engage in prohibited activities. You may also terminate your account at any 
                        time by contacting us.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><FileText size={18} /></span>
                        Changes to Terms
                    </div>
                    <p className="terms-text">
                        We may update these Terms from time to time. We will notify you of significant changes via 
                        email or through our platform. Continued use of our services after changes constitutes 
                        acceptance of the updated Terms.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><CheckCircle size={18} /></span>
                        Contact Information
                    </div>
                    <p className="terms-text">
                        For questions about these Terms & Conditions, contact us at:<br />
                        <strong>Email:</strong> c84289798@gmail.com<br />
                        <strong>Location:</strong> Ahmedabad, India
                    </p>
                </div>
            </div>
        </div>
    );
}

export default TermsAndConditions;